import { useEffect, useState } from "react";
import { backend_url } from "../../App";
import AddReview from "./AddReview";
import AllReviewsPopup from "./AllReviewsPopup";

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={i < rating ? "text-yellow-400" : "text-gray-200"}>★</span>
    ))}
  </div>
);

const Reviews = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showAllPopup, setShowAllPopup] = useState(false);

  useEffect(() => {
    fetch(`${backend_url}/api/customerreview`)
      .then((res) => res.json())
      .then((data) => {
        const filtered = data.filter(
          (r) => r.productId === productId && (r.showReview === true || r.showHide === true)
        );
        setReviews(filtered);
      })
      .catch((err) => console.error("Failed to load reviews", err));
  }, [productId]);

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div
      className="relative overflow-hidden px-4 sm:px-8 py-12"
      style={{ background: "linear-gradient(180deg, var(--brand-surface) 0%, var(--brand-bg) 100%)" }}
    >
      {/* Top purple line */}
      <div className="absolute top-0 left-0 right-0 h-0.5"
           style={{ background: "linear-gradient(90deg, transparent, var(--brand-primary-500), transparent)" }} />

      <div className="relative max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-8 gap-2">
          <span
            className="text-white text-xs font-bold px-4 py-1 rounded-full"
            style={{ background: "linear-gradient(90deg, var(--brand-primary-600), var(--brand-secondary-500))" }}
          >
            ⭐ Customer Reviews
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 text-center"
            style={{ fontFamily: "'Times New Roman', Serif" }}
          >
            What Our Customers Say
          </h2>
          <div className="flex items-center gap-3">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[var(--brand-primary-400)]" />
            <div className="w-1.5 h-1.5 rotate-45 bg-[var(--brand-primary-500)]" />
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[var(--brand-primary-400)]" />
          </div>
        </div>

        {/* Stats + action row */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8
                        bg-white rounded-2xl border border-[var(--brand-primary-100)] shadow-sm px-6 py-4">
          {avgRating ? (
            <div className="flex items-center gap-3">
              <span className="text-4xl font-bold text-[var(--brand-primary-700)]">{avgRating}</span>
              <div>
                <StarRating rating={Math.round(avgRating)} />
                <p className="text-xs text-gray-400 mt-0.5">{reviews.length} review{reviews.length !== 1 ? "s" : ""}</p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400">No ratings yet</p>
          )}

          <div className="flex gap-3">
            {reviews.length > 4 && (
              <button
                onClick={() => setShowAllPopup(true)}
                className="px-4 py-2 rounded-xl border-2 border-[var(--brand-primary-400)] text-[var(--brand-primary-700)] text-sm font-semibold hover:bg-[var(--brand-primary-50)] transition"
              >
                View All
              </button>
            )}
            <button
              onClick={() => setShowAddPopup(true)}
              className="px-5 py-2 rounded-xl text-white text-sm font-semibold shadow hover:opacity-90 transition"
              style={{ background: "linear-gradient(90deg, var(--brand-primary-600), var(--brand-secondary-500))" }}
            >
              ✍️ Write a Review
            </button>
          </div>
        </div>

        {/* Reviews grid or empty state */}
        {reviews.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 bg-white rounded-2xl border border-[var(--brand-primary-100)]">
            <span className="text-5xl">💬</span>
            <p className="text-gray-500 font-medium">No reviews yet.</p>
            <p className="text-[var(--brand-primary-600)] text-sm font-semibold">Be the first to write a review!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {reviews.slice(0, 4).map((review) => (
              <div
                key={review._id}
                className="relative overflow-hidden flex flex-col bg-white rounded-2xl border border-[var(--brand-primary-100)] shadow-sm
                           hover:shadow-[0_8px_24px_rgba(124,58,237,0.15)] hover:border-[var(--brand-primary-300)] hover:-translate-y-0.5
                           transition-all duration-300"
              >
                {/* Gradient accent strip (matches the form header) */}
                <div className="h-1.5 w-full" style={{ background: "linear-gradient(90deg,#5B2B9E,#7A4CC0)" }} />

                <div className="relative flex flex-col gap-3 p-4">
                  {/* Faint quote mark */}
                  <span className="absolute top-2 right-3 text-4xl leading-none pointer-events-none select-none"
                        style={{ fontFamily: "Georgia, serif", color: "#F1E9FB" }}>&rdquo;</span>

                  {/* Reviewer header */}
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm"
                         style={{ background: "linear-gradient(135deg, var(--brand-primary-600), var(--brand-secondary-500))" }}>
                      {review.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate leading-tight">
                        {review.name.charAt(0).toUpperCase() + review.name.slice(1)}
                      </p>
                      <StarRating rating={review.rating} />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-sm text-gray-900 leading-snug">
                    {review.reviewTitle.charAt(0).toUpperCase() + review.reviewTitle.slice(1)}
                  </h3>

                  {/* Body */}
                  <p className="text-gray-600 text-xs leading-relaxed flex-1">
                    "{review.review.charAt(0).toUpperCase() + review.review.slice(1)}"
                  </p>

                  {/* Image */}
                  {review.reviewImage && (
                    <img
                      crossOrigin="anonymous"
                      src={review.reviewImage}
                      alt="Review"
                      className="rounded-xl object-cover w-full h-32 mt-1 border border-[var(--brand-primary-50)]"
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAddPopup && <AddReview onClose={() => setShowAddPopup(false)} productId={productId} />}
      {showAllPopup && <AllReviewsPopup reviews={reviews} onClose={() => setShowAllPopup(false)} />}
    </div>
  );
};

export default Reviews;
